import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { formatIDR, GOZIS_WA_URL } from "../lib/constants";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ArrowLeft, MessageCircle, QrCode } from "lucide-react";

export default function KalkulatorZakat() {
  const [tab, setTab] = useState("profesi");
  const [goldPrice, setGoldPrice] = useState("1350000");
  const [income, setIncome] = useState("");
  const [otherIncome, setOtherIncome] = useState("");
  const [wealth, setWealth] = useState("");
  const [debt, setDebt] = useState("");

  const gold = Number(goldPrice) || 0;
  const nisabYear = 85 * gold;
  const nisabMonth = nisabYear / 12;

  const profesi = useMemo(() => {
    const total = (Number(income) || 0) + (Number(otherIncome) || 0);
    const wajib = total >= nisabMonth && nisabMonth > 0;
    return { total, wajib, zakat: wajib ? total * 0.025 : 0, nisab: nisabMonth };
  }, [income, otherIncome, nisabMonth]);

  const maal = useMemo(() => {
    const total = (Number(wealth) || 0) - (Number(debt) || 0);
    const wajib = total >= nisabYear && nisabYear > 0;
    return { total, wajib, zakat: wajib ? total * 0.025 : 0, nisab: nisabYear };
  }, [wealth, debt, nisabYear]);

  const ResultCard = ({ r, periode }) => (
    <div className={`rounded-xl border p-5 text-center ${r.wajib ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-slate-50"}`} data-testid={`zakat-result-${periode}`}>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Nisab ({periode}): {formatIDR(r.nisab)}</p>
      {r.wajib ? (
        <>
          <p className="mt-3 text-sm text-slate-600">Anda <b>wajib</b> menunaikan zakat sebesar</p>
          <p className="mt-1 text-3xl font-bold font-heading text-emerald-800">{formatIDR(r.zakat)}</p>
          <p className="text-xs text-slate-500">2,5% × {formatIDR(r.total)}</p>
        </>
      ) : (
        <p className="mt-3 text-sm text-slate-600">
          {r.total > 0 ? "Belum mencapai nisab — Anda belum wajib zakat, namun dianjurkan berinfaq." : "Isi data di atas untuk menghitung."}
        </p>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="kalkulator-page">
      <header className="border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-3">
          <Link to="/"><img src="/logo-lazisnu.png" alt="Logo LAZISNU Dawe" className="h-10 w-10 rounded-lg bg-white object-contain p-0.5" /></Link>
          <div className="flex-1">
            <p className="font-heading font-bold text-slate-900 leading-tight">Kalkulator Zakat</p>
            <p className="text-xs text-slate-500">NU Care LAZISNU Kecamatan Dawe</p>
          </div>
          <Link to="/">
            <Button variant="outline" size="sm" data-testid="kalkulator-back-button">
              <ArrowLeft className="mr-1.5 h-4 w-4" /> Kembali
            </Button>
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-8 space-y-6">
        <Card>
          <CardContent className="p-5">
            <div className="space-y-2">
              <Label>Harga Emas per Gram (Rp) — acuan nisab 85 gram</Label>
              <Input type="number" data-testid="zakat-gold-input" value={goldPrice} onChange={(e) => setGoldPrice(e.target.value)} />
            </div>
          </CardContent>
        </Card>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="zakat-tabs">
            <TabsTrigger value="profesi" data-testid="zakat-tab-profesi">Zakat Profesi</TabsTrigger>
            <TabsTrigger value="maal" data-testid="zakat-tab-maal">Zakat Maal</TabsTrigger>
          </TabsList>
        </Tabs>

        {tab === "profesi" ? (
          <Card data-testid="zakat-profesi-card">
            <CardHeader><CardTitle className="text-lg">Zakat Profesi (bulanan)</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Penghasilan per Bulan (Rp)</Label>
                  <Input type="number" data-testid="zakat-income-input" value={income} onChange={(e) => setIncome(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Penghasilan Lain per Bulan (Rp)</Label>
                  <Input type="number" data-testid="zakat-other-income-input" value={otherIncome} onChange={(e) => setOtherIncome(e.target.value)} />
                </div>
              </div>
              <ResultCard r={profesi} periode="per bulan" />
            </CardContent>
          </Card>
        ) : (
          <Card data-testid="zakat-maal-card">
            <CardHeader><CardTitle className="text-lg">Zakat Maal (tahunan / haul)</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Total Harta Tersimpan 1 Tahun (Rp)</Label>
                  <Input type="number" data-testid="zakat-wealth-input" value={wealth} onChange={(e) => setWealth(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Utang Jatuh Tempo (Rp)</Label>
                  <Input type="number" data-testid="zakat-debt-input" value={debt} onChange={(e) => setDebt(e.target.value)} />
                </div>
              </div>
              <ResultCard r={maal} periode="per tahun" />
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Link to="/qris">
            <Button variant="outline" className="w-full" data-testid="zakat-goto-qris">
              <QrCode className="mr-2 h-4 w-4 text-emerald-800" /> Bayar via QRIS
            </Button>
          </Link>
          <a href={GOZIS_WA_URL} target="_blank" rel="noreferrer">
            <Button className="w-full bg-emerald-800 hover:bg-emerald-700 text-white" data-testid="zakat-goto-gozis">
              <MessageCircle className="mr-2 h-4 w-4" /> Jemput Zakat (Go-ZIS)
            </Button>
          </a>
        </div>
      </main>
    </div>
  );
}
